import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { useEffect } from 'react';
import { firebaseAuth, firestore } from './firebase';
import { handleFirestoreUpdates } from '../utils/whatsAppchatbotUtils';
import { setChatBuckets } from '../reducers/whatsappChatbotSlice';
import { IChatListItem, ICosmosSyncData } from '../pages/WhatsappChatbot/types';
import { signInToFirebase } from '../pages/auth/AuthActions';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import {
  isFunction,
  logCustomNegativeExperience,
  toFixedValueNotation
} from '@cp/utils/commonUtils';
import { firebaseCollectionPaths } from '@cp/src/types/CommonConstans';
import {
  CosmosSyncBlockStatus,
  setCosmosSyncBlockStatus,
  setIsCosmosUpdateRequired,
  setLastSyncPermissionStatus,
  setLastSyncTimestamp,
  setUserAppVersion
} from '../reducers/commonSlice';
import { LOCAL_STORAGE_KEYS } from '@cp/constants/StorageKeys';
import logger from '../utils/logger';
import { FIREBASE_CONNECTION_LATENCY_THRESHOLD, GLOBAL_STATUS } from '../constants/Global';

const useFirestoreUpdates = () => {
  const agentRefId = useSelector((state: RootState) => state.common.userData?.referenceId);
  const agentRoles = useSelector((state: RootState) => state.common.userData?.roles);
  const cosmosLatestVersion = useSelector(
    (state: RootState) => state.common.cosmosVersionData?.appVersion
  );
  const isCosmosUpdateRequired = useSelector(
    (state: RootState) => state.common.isCosmosUpdateRequired
  );
  const longhornFirebaseToken = useSelector(
    (state: RootState) => state.common.userData?.longhornFirebaseToken
  );
  const featureFlags = useSelector((store: RootState) => store?.common?.featureFlags);

  const { cosmosSyncOnLonghornFeatureFlag } = featureFlags || {};
  const getFirebaseCurrentUser = () => {
    return firebaseAuth().currentUser;
  };
  const isChatUser =
    agentRoles?.includes(Roles.ROLE_HUMAN_REMINDER_CHAT_AGENT) ||
    agentRoles?.includes(Roles.ROLE_TELE_CHAT_AGENT);
  let chatCollectionRef: () => void;
  let cosmosSyncCollectionRef: () => void;

  const dispatch = useDispatch();

  const listenToComosSyncChanges = () => {
    cosmosSyncCollectionRef = firestore
      .collection(firebaseCollectionPaths.cosmosSync.path)
      .doc(agentRefId)
      .onSnapshot(querySnapshot => {
        const data = querySnapshot.data() as ICosmosSyncData;
        logger({
          msg: 'got firebase update',
          extras: {
            lastSyncTimeStamp: String(data?.lastSyncTimestamp),
            appVersion: data?.appVersion
          }
        });

        if (!data && cosmosSyncOnLonghornFeatureFlag) {
          dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.BLOCKED));
        }

        if (data?.appVersion && cosmosSyncOnLonghornFeatureFlag) {
          if (cosmosLatestVersion) {
            const isCosmosVersionMatched = data.appVersion === cosmosLatestVersion;
            const shouldUpdateRequired = !isCosmosVersionMatched;

            if (shouldUpdateRequired !== isCosmosUpdateRequired) {
              dispatch(setUserAppVersion(data?.appVersion));
              dispatch(setIsCosmosUpdateRequired(shouldUpdateRequired));
            }
          } else if (isCosmosUpdateRequired) {
            dispatch(setIsCosmosUpdateRequired(false));
          }
        }
        if (data) {
          dispatch(setLastSyncTimestamp(data?.lastSyncTimestamp || undefined));
          dispatch(setLastSyncPermissionStatus(data?.allPermissionsGranted));
        }
      });
  };

  const listenToFirestoreChatChanges = () => {
    chatCollectionRef = firestore
      .collection(firebaseCollectionPaths.agents.path)
      .doc(agentRefId)
      .collection(firebaseCollectionPaths.agents.subCollections.whatsAppChat.path)
      .onSnapshot(querySnapshot => {
        const addedCases: IChatListItem[] = [];
        const modifiedCases: IChatListItem[] = [];
        const removedCases: IChatListItem[] = [];
        const reset = false;
        querySnapshot.docChanges().forEach(docSnapshot => {
          handleFirestoreUpdates(docSnapshot, addedCases, modifiedCases, removedCases);
        });
        dispatch(setChatBuckets({ addedCases, modifiedCases, removedCases, reset }));
      });
  };

  const trackConnectionLatency = () => {
    const connectionStart = performance.now();
    let connectionLogged = false;

    const testRef = firestore.collection(firebaseCollectionPaths.agents.path).doc(agentRefId);

    const unsubscribe = testRef.onSnapshot(
      () => {
        if (!connectionLogged) {
          const latency = performance.now() - connectionStart;

          if (latency > FIREBASE_CONNECTION_LATENCY_THRESHOLD) {
            logCustomNegativeExperience({
              message: 'Slow Firebase connection establishment',
              extras: {
                latency: toFixedValueNotation(latency),
                type: 'connection_establishment'
              }
            });
          }
          connectionLogged = true;
          unsubscribe();
        }
      },
      error => {
        if (!connectionLogged) {
          const latency = performance.now() - connectionStart;

          logCustomNegativeExperience({
            message: 'Firebase connection failed',
            extras: {
              latency: toFixedValueNotation(latency),
              error: error.message,
              type: 'connection_failure'
            }
          });
          connectionLogged = true;
        }
      }
    );
  };

  useEffect(() => {
    // Add offline/online event listeners for Firebase
    const handleOffline = () => {
      firestore.disableNetwork().catch(error => {});
    };
    const handleOnline = () => {
      firestore.enableNetwork().catch(error => {});
    };

    window.addEventListener(GLOBAL_STATUS.OFFLINE, handleOffline);
    window.addEventListener(GLOBAL_STATUS.ONLINE, handleOnline);

    //register firebase listener
    firebaseAuth().onAuthStateChanged(user => {
      if (user) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.IS_FIREBASE_SIGNED_IN, 'true');

        // Track connection establishment latency
        trackConnectionLatency();
      }
      if (isChatUser) {
        listenToFirestoreChatChanges();
      }
      listenToComosSyncChanges();
    });
    //sign in to firebase if not already signed in
    if (getFirebaseCurrentUser()) {
      return;
    } else {
      signInToFirebase(longhornFirebaseToken || '', () => {
        if (isChatUser) {
          listenToFirestoreChatChanges();
        }
        listenToComosSyncChanges();
      });
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [agentRefId, longhornFirebaseToken]);

  return () => {
    isFunction(chatCollectionRef) && chatCollectionRef();
    isFunction(cosmosSyncCollectionRef) && cosmosSyncCollectionRef();
  };
};

export default useFirestoreUpdates;
